<template>
  <div
    v-if="!isBlockedDomainOrMid"
    v-show="isReady"
    id="qp-container"
    class="qp-container"
    :class="{
      'qp--align-center': alignment == 'center',
      'qp--align-right': alignment == 'right',
      'qp--logo-first': isLogoFirst,
      'qp--align-left': alignment == 'left',
    }"
    :style="[
      customMargins ? { margin: customMargins } : {},
      size ? { 'font-size': size } : {},
      { fontFamily: zipWidgetFontStyle },
      isDarkMode ? { color: 'white' } : {},
    ]"
  >
    <span
      v-if="isLogoFirst"
      ref="qp-logo-container"
      id="qp-logo-first"
      class="qp-open-modal-button"
      @click="displayModal"
      @keydown.enter="displayModal"
      tabindex="0"
    ></span>
    <span
      :class="{ 'qp--nowrap': true, 'qp__or-text-hidden': applyGrayLabel }"
      v-if="
        (!isLogoFirst && !subtextLayout && !is4Installments) ||
          shouldShowPayIn8
      "
      data-i18n="widget.or"
      >or</span
    >
    <span class="qp-subtext-layout qp--nowrap" v-if="subtextLayout">
      <span class="qp--nowrap" data-i18n="widget.primary-text"
        >or buy now pay later</span
      >
      <span class="qp--nowrap" style="margin-right: 0;">
        <span data-i18n="widget.with"> with </span>
        <span
          @click="displayModal"
          @keydown.enter="displayModal"
          ref="qp-logo-container"
          id="qp-logo-last"
          :class="{
            'qp-open-modal-button': true,
            'qp--ada-disabled': disableAdaCompliance,
          }"
        ></span>
      </span>
      <span
        @click="displayModal"
        @keydown.enter="displayModal"
        :class="{
          'qp-info-icon': true,
          'qp-open-modal-button': true,
          'qp--ada-disabled': disableAdaCompliance,
        }"
        tabindex="0"
      >
        <svg
          alt="Info icon, when clicked, opens infographic about the option of buying this item with 4 installment payments"
          version="1.1"
          width="15"
          height="15"
          viewBox="0 0 15 15"
          xmlns="http://www.w3.org/2000/svg"
        >
          <title>QuadPay - Pay in 4 installments, no interest.</title>
          <g fill="none" fill-rule="evenodd">
            <g>
              <path
                d="M7 10V6.125"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M7 12.625C10.1066 12.625 12.625 10.1066 12.625 7C12.625 3.8934 10.1066 1.375 7 1.375C3.8934 1.375 1.375 3.8934 1.375 7C1.375 10.1066 3.8934 12.625 7 12.625Z"
                stroke="black"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <circle
                cx="7"
                cy="4.5"
                r="0.25"
                stroke="black"
                stroke-width="0.5"
              />
            </g>
          </g>
        </svg>
      </span>
    </span>

    <!-- pay-in-n widget verbiage -->
    <template v-if="shouldShowPayIn8">
      <template v-if="shouldShowPayInNMin">
        <div class="qp--nowrap">pay in installments on orders</div>
        <div class="qp--line-break"></div>
        <div class="qp--nowrap">
          over
          <span
            class="qp-price"
            :style="priceColor ? { color: priceColor } : {}"
            >{{ formatUSDCurrency(paymentSchedulesMin) }}</span
          >
        </div>
      </template>
      <template v-else-if="shouldShowPayInNMax">
        <div class="qp--nowrap">pay in installments on orders</div>
        <div class="qp--line-break"></div>
        <div class="qp--nowrap">
          under
          <span
            class="qp-price"
            :style="priceColor ? { color: priceColor } : {}"
            >{{ formatUSDCurrency(paymentSchedulesMax) }}</span
          >
        </div>
      </template>
      <template v-else>
        <div class="qp--nowrap">
          pay in installments as low as
          <span
            class="qp-price"
            :style="priceColor ? { color: priceColor } : {}"
            >{{
              formatUSDCurrency(lowestPaymentScheduleInstallmentAmount)
            }}</span
          >
        </div>
        <div v-if="!singleLine" class="qp--line-break"></div>
      </template>
    </template>

    <!-- Classic pay-in-4 widget verbiage -->
    <template v-else>
      <span class="qp--nowrap">
        <span>{{ widgetVerbiageTranslated }}</span>
      </span>
      <span
        v-if="
          shouldShowInstallmentAmount ||
            (!shouldShowInstallmentAmount && hasValidAmount)
        "
        class="qp--nowrap"
        id="displayCustom"
      >
        <span
          v-show="totalAmount <= max && totalAmount >= min"
          data-i18n="widget.price-separator-of"
          >of</span
        >
        <span
          v-show="totalAmount > max"
          data-i18n="widget.price-separator-up-to"
          >on orders up to</span
        >
        <span v-show="totalAmount < min" data-i18n="widget.price-separator-over"
          >on orders over</span
        >
        <span
          id="qp-price"
          class="qp-price"
          :style="priceColor ? { color: priceColor } : {}"
        >
          <span v-if="totalAmount <= max && totalAmount >= min"
            ><span data-i18n="currency.number">$</span
            >{{ installmentAmount }}</span
          >
          <span v-else-if="totalAmount > max"
            ><span data-i18n="currency.number">$</span>{{ max }}</span
          >
          <span v-else-if="totalAmount < min"
            ><span data-i18n="currency.number">$</span>{{ min }}</span
          >
        </span>
      </span>
    </template>

    <div v-if="applyGrayLabel" class="qp--line-break"></div>
    <span
      v-if="!isLogoFirst && !subtextLayout"
      class="qp--nowrap"
      :class="{
        'qp--wrapped-content': hasAlignment,
        'qp--align-center': alignment == 'center',
        'qp--align-right': alignment == 'right',
        'qp--align-left': alignment == 'left',
      }"
    >
      <span
        v-if="
          displayPromotions === 'display_promotions' &&
            promotionObject &&
            promotionMinimum
        "
        class="qp-promotional-discount"
      >
        <span class="qp-discount-plus">+</span>
        <span data-i18n="widget.promo-spend">spend</span>
        <span>${{ this.promotionObject.orderAmount }},</span>
        <span data-i18n="widget.promo-get">get</span>
        <span>${{ this.promotionObject.promotionDiscount }}</span>
        <span data-i18n="widget.promo-off">off</span>
      </span>
      <span
        v-if="
          displayPromotions === 'display_promotions' &&
            promotionObject &&
            !promotionMinimum
        "
        class="qp-promotional-discount"
      >
        <span>+ ${{ this.promotionObject.promotionDiscount }}</span>
        <span data-i18n="widget.promo-off">off</span>
      </span>
      <span data-i18n="widget.with"> with </span>
      <img
        v-if="applyGrayLabel"
        class="qp-gray-label-logo"
        :src="logoUrl"
        :alt="modalGrayLabelName"
        :title="modalGrayLabelName"
      />
      <span
        @click="displayModal"
        @keydown.enter="displayModal"
        ref="qp-logo-container"
        id="qp-logo-last"
        class="qp-open-modal-button"
      ></span>
      <span
        v-if="displayPromotions == 'display_promotions'"
        @click="displayModal"
        @keydown.enter="displayModal"
        class="qp-open-modal-button qp-terms-link"
        data-i18n="widget.promo-terms"
        >Terms apply</span
      >
      <template v-else>
        <button
          v-if="isLearnMoreButtonFeatureEnabled"
          @click="displayModal"
          @keydown.enter="displayModal"
          class="qp-open-modal-button learn-more"
        >
          Learn more
        </button>
        <span
          v-else
          @click="displayModal"
          @keydown.enter="displayModal"
          class="qp-open-modal-button qp-info-icon"
          ref="qp-info-icon-container"
        ></span>
      </template>
    </span>
    <template
      v-if="!subtextLayout &&
      isLogoFirst &&
      displayPromotions != 'display_promotions'"
    >
      <button
        v-if="isLearnMoreButtonFeatureEnabled"
        @click="displayModal"
        @keydown.enter="displayModal"
        class="qp-open-modal-button learn-more"
      >
        Learn more
      </button>
      <span
        v-else
        @click="displayModal"
        @keydown.enter="displayModal"
        class="qp-open-modal-button qp-info-icon"
        ref="qp-info-icon-container"
      ></span>
    </template>
    <span
      v-if="
        displayPromotions === 'display_promotions' &&
          isLogoFirst &&
          promotionObject &&
          !promotionMinimum
      "
      class="qp-promotional-discount"
    >
      <span>+ ${{ this.promotionObject.promotionDiscount }}</span>
      <span data-i18n="widget.promo-off">off</span>
    </span>
    <span
      v-if="
        displayPromotions === 'display_promotions' &&
          isLogoFirst &&
          promotionObject &&
          promotionMinimum
      "
      class="qp-promotional-discount"
    >
      <span class="qp-discount-plus">+</span>
      <span data-i18n="widget.promo-spend">spend</span>
      <span>${{ this.promotionObject.orderAmount }},</span>
      <span data-i18n="widget.promo-get">get</span>
      <span>${{ this.promotionObject.promotionDiscount }}</span>
      <span data-i18n="widget.promo-off">off</span>
    </span>
    <span
      v-if="displayPromotions == 'display_promotions' && isLogoFirst"
      @click="displayModal"
      @keydown.enter="displayModal"
      class="qp-open-modal-button qp-terms-link"
      data-i18n="widget.promo-terms"
      >Terms apply</span
    >
  </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import getConfiguration, {
  EnvironmentConfiguration,
} from "@/shared/configuration";
import widgetApi from "@/widget/widget-api";
import promotions from "@/widget/promotions";
import {
  parseFullAmount,
  calcInstallmentAmount,
  calculateCustomerFee,
} from "@/widget/shared-api";
import { _createModal } from "@/widget/quadpay-modal";
import { _createLogo } from "@/widget/quadpay-logo";
import { _createInfoIcon } from "@/widget/quadpay-info-icon";
import Logo from "@/widget/assets/components/ZipLogo.vue";
import Modal from "@/widget/assets/components/BaseModal.vue";
import InfoIcon from "@/widget/assets/components/InfoIcon.vue";
import {
  BaseModalOptions,
  LogoOptions,
  InfoIconOptions,
  PromotionObject,
} from "@/widget/widget-models";
import { v4 as uuidv4 } from "uuid";
import widgetScss from "@/widget/assets/styles/widget.scss";
import logger from "@/shared/logger";
import Translator from "@/shared/translator";
import baseModalScss from "@/widget/assets/styles/base-modal.scss";
import payInNModalScss from "@/widget/assets/styles/pay-in-n-modal.scss";
import { BaseWidget } from "@/widget/base-widget";
import { getEventBus } from "@/services/events";
import { getAnalytics, Options, WidgetType } from "@/services/analytics";
import {
  ExperimentTextVariationKey,
  FeatureFlagKey,
} from "@/services/widget-data/models";
import { getWidgetData } from "@/services/widget-data";
import { isValidAmount } from "@/utils/isValidAmount";
import { PaymentSchedule } from "@/services/gateway/models";
import PayInNModal from "./PayInNModal.vue";
import { parse } from "path";

const ignoreWarnMessage =
  "Do not use built-in or reserved HTML elements as component";
Vue.config.warnHandler = (msg: string, vm: Vue, trace: string) => {
  if (!msg?.startsWith(ignoreWarnMessage)) {
    logger.info(`Vue warn handler: ${msg}`, vm, msg, trace);
  }
};

@Component
export default class Widget extends BaseWidget {
  // Only select components that aren't being used already for multiple loads
  widgetId = uuidv4();
  url = window.location.href.split("?")[0];
  totalAmount = 0;
  installmentAmount = "0.00";
  modalComponentObject; // Vue props of the modal can be modified through this object's properties. ex: modalComponentObject.purchaseAmount = 500
  logoComponentObject;
  infoIconComponentObject;
  applyGrayLabel = false;
  timelineColor = "";
  modalTopColor = "";
  linkTextColor = "";
  grayLabelModalCopy = "";
  modalHeadersFontStyle = "";
  modalBodyFontStyle = "";
  paymentWidgetHeaderFontStyle = "";
  paymentWidgetBodyFontStyle = "";
  zipWidgetFontStyle = "";
  modalGrayLabelName = "";
  logoUrl = "";
  translate = new Translator();
  isReady = false;
  eventBus = getEventBus();
  startLoad = 0;
  loadTimeMilliseconds = 0;
  widgetVerbiageTranslated = "";
  widgetVerbiageKey = "";
  isThereTextVariation = false;
  showTilaTerm = false;
  shouldShowInstallmentAmount = true;
  hasModalOpened = false;
  widgetOnlyVerbiage = "";
  displayPromotions = "";
  promotionMinimum = false;
  promotionObject: PromotionObject;
  fireEventsIfTriggerTermsExperiment = false;
  shopifyWindowObject = (window as any).Shopify;
  configuration: EnvironmentConfiguration;
  hasValidAmount = true;
  paymentSchedules: PaymentSchedule[] = [];
  modalType: typeof Modal | typeof PayInNModal;
  isPayIn8FeatureEnabled = false;
  isLearnMoreButtonFeatureEnabled = false;

  async created() {
    this.configuration = getConfiguration();
    this.startLoad = performance.now();
    this.componentWidget = document.querySelector(
      `${this.configuration.tagname}:not([widgetid])`
    );

    // Add stylesheet to shadowroot - set widget ID so styles only applied once
    this.componentWidget.setAttribute("widgetid", this.widgetId);
    if (this.componentWidget?.shadowRoot) {
      const stylesheet = document.createElement("style");
      stylesheet.innerHTML = `${widgetScss}`;
      this.componentWidget.shadowRoot.append(stylesheet);
    }
  }

  async mounted() {
    if (this.isBlockedDomainOrMid) {
      return;
    }

    this.initializeAmount(this.amount);

    if (this.configuration.isWidgetDataFetchingEnabled) {
      await this.setupWidgetData();
      // call this again now that we've fetched new data
      this.initializeAmount(this.amount);
    }


    if (this.configuration.isAnalyticsEnabled) {
      const analytics = getAnalytics();
      if (this.merchantId) analytics.merchantId = this.merchantId;
    }

    if (this.translatorLanguage)
      this.translate.setLanguage(this.translatorLanguage);
    await this.translate.translateComponent(
      this.currency,
      this.componentWidget
    );

    this.calculateWidgetVerbiage();

    if (this.breakpointAlignment) this.handleMediaQuery();

    // Update price on change
    if (this.amountSelector) this.listenForAmountChange();

    this.insertLogo();
    this.insertInfoIcon();

    // only show new modal if we got payment schedules from widget data
    if (this.shouldShowPayIn8) {
      this.insertPayInNModal();
    } else {
      this.insertBaseModal();
    }

    const canPublishEvents =
      (this.configuration.isAnalyticsEnabled ||
        (this.fireEventsIfTriggerTermsExperiment &&
          this.configuration.experimentsForceEvents)) &&
      this.displayPromotions !== "display_promotions";
    if (canPublishEvents) {
      const options = {
        root: null,
        rootMargin: "0px",
        threshold: 0.5,
      };

      const observer = new IntersectionObserver(
        this.handleIntersection,
        options
      );
      observer.observe(this.componentWidget);
      this.loadTimeMilliseconds = Math.round(
        performance.now() - this.startLoad
      );
    }

    this.isReady = true;
  }

  async fetchPaymentSchedules() {
    // only fetch payment schedules again if amount has changed since loading
    if (this.totalAmount && this.isPayIn8FeatureEnabled) {
      try {
        const paymentScheduleResponse = await getWidgetData().fetchPaymentSchedules(
          this.totalAmount.toString()
        );
        this.paymentSchedules = paymentScheduleResponse;
        this.insertPayInNModal();
      } catch (error) {
        logger.error(error);
      }
    } else {
      logger.warn(
        "Could not get payment schedules, because no merchantId was added to the widget"
      );
    }
  }

  // Props
  @Prop() amount: number;

  // Watchers
  @Watch("amount")
  onPropertyChange(value) {
    this.handleAmountUpdate(value);
  }

  initializeAmount(amount) {
    // if no initial amount passed in, check amount selector
    if (!isValidAmount(amount) && this.amountSelector) {
      amount = this.getAmountSelectorValue();
    }

    if (isValidAmount(amount)) {
      let parsedAmount = parseFullAmount(amount);
      let installmentFee;
      let shouldApplyFees = false;
      if (this.isFeeMerchant) {
        installmentFee =
          calculateCustomerFee(parsedAmount, this.merchantFeeTiers) / 4;
        shouldApplyFees = true;
      } else {
        installmentFee = this.installmentServiceFeeAmount;
        shouldApplyFees = this.isServiceFeeMerchant;
      }

      this.installmentAmount = calcInstallmentAmount(
        parsedAmount,
        installmentFee,
        shouldApplyFees
      );
      this.totalAmount = parsedAmount;
      this.hasValidAmount = true;
    } else {
      this.hasValidAmount = false;
      this.installmentAmount = "0.00";
      this.totalAmount = 0;
    }
  }

  formatUSDCurrency(amount) {
    let USDollarFormat = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });

    return USDollarFormat.format(amount);
  }

  get shouldShowPayIn8(): boolean {
    return this.doesMerchantHavePayInN && this.isPayIn8FeatureEnabled;
  }

  // Determines if merchant has pay-in-n (greater than default 4)
  get doesMerchantHavePayInN(): boolean {
    return !!this.paymentSchedules?.length;
  }

  // gets lowest minimum of all payment schedules
  get paymentSchedulesMin(): number | undefined {
    const min = this.paymentSchedules.reduce((min, paymentSchedule) => {
      if (paymentSchedule.minimumOrderAmount < min)
        min = paymentSchedule.minimumOrderAmount;
      return min;
    }, Infinity);

    if (min === Infinity) return undefined;
    else return min;
  }

  // gets highest maximum of all payment schedules
  get paymentSchedulesMax(): number | undefined {
    const max = this.paymentSchedules.reduce((max, paymentSchedule) => {
      if (paymentSchedule.maximumOrderAmount > max)
        max = paymentSchedule.maximumOrderAmount;
      return max;
    }, 0);

    if (max === 0) return undefined;
    else return max;
  }

  get lowestPaymentScheduleInstallmentAmount(): number | undefined {
    const lowestInstallmentAmount = this.paymentSchedules.reduce(
      (lowest, paymentSchedule) => {
        const lastIndex = paymentSchedule?.installments?.length - 1;
        if (
          paymentSchedule?.installments?.[lastIndex]?.totalAmountIncludingFees <
          lowest
        ) {
          lowest =
            paymentSchedule.installments[lastIndex].totalAmountIncludingFees;
        }

        return lowest;
      },
      Infinity
    );

    if (lowestInstallmentAmount === Infinity) return undefined;
    else return lowestInstallmentAmount;
  }

  get shouldShowPayInNMin(): boolean {
    return (
      this.paymentSchedulesMin !== undefined &&
      this.totalAmount < this.paymentSchedulesMin
    );
  }

  get shouldShowPayInNMax(): boolean {
    return (
      this.paymentSchedulesMax !== undefined &&
      this.totalAmount > this.paymentSchedulesMax
    );
  }

  get logoOptions(): LogoOptions {
    return {
      disableAdaCompliance: this.disableAdaCompliance,
      logoAlignment: this.logoAlignment,
      logoOption: this.logoOption
        ? this.logoOption
        : this.isDarkMode
        ? "dark-mode"
        : null,
      logoColor: this.logoColor,
      logoTextColor: this.logoTextColor,
      logoSize: this.logoSize,
      hideLogo: this.hideLogo,
      isLogoFirst: this.isLogoFirst,
      applyGrayLabel: this.applyGrayLabel,
    };
  }

  // Vue-Props for Base modal
  get baseModalOptions(): BaseModalOptions {
    let isServiceFeeMerchant: boolean = false;
    let isMFPPMerchant: boolean = false;

    if (!this.isWebBankMerchant) {
      isServiceFeeMerchant = this.isServiceFeeMerchant;
      isMFPPMerchant = this.isMFPPMerchant;
    }

    return {
      isServiceFeeMerchant: isServiceFeeMerchant,
      isMFPPMerchant: isMFPPMerchant,
      serviceFeeAmount: parseFullAmount(this.installmentServiceFeeAmount),
      disableAdaCompliance: this.disableAdaCompliance,
      minimumOrderAmount: this.minModal
        ? this.minModal.toString()
        : this.min.toString(),
      learnMoreUrl: this.learnMoreUrl,
      implementCloseModal: this.implementCloseModal,
      widgetId: this.widgetId,
      modalSubtitle: this.widgetVerbiageKey,
      merchantName: this.merchantName,
      currency: this.currency,
      translatorLanguage: this.translatorLanguage,
      modalTopColor: this.modalTopColor,
      linkTextColor: this.linkTextColor,
      applyGrayLabel: this.applyGrayLabel,
      modalHeadersFontStyle: this.modalHeadersFontStyle,
      modalBodyFontStyle: this.modalBodyFontStyle,
      modalGrayLabelName: this.modalGrayLabelName,
      logoUrl: this.logoUrl,
      isThereTextVariation: this.isThereTextVariation,
      shouldShowWebbankDisclosure: this.shouldShowWebbankDisclosure,
      isMobileSDK: this.isMobileSDK,
      displayPromotions: this.displayPromotions,
      promotionObject: this.promotionObject,
      shouldShowWebbankFeeDisclosure: this.shouldShowWebbankFeeDisclosure,
    };
  }

  get infoIconOptions(): InfoIconOptions {
    return {
      // displayModal: this.displayModal,
      disableAdaCompliance: this.disableAdaCompliance,
    };
  }

  get isBlockedDomainOrMid() {
    return (
      this.configuration.killSwitchDomains.some((domain) =>
        window.location.hostname.includes(domain)
      ) || this.configuration.killSwitchMid.includes(this.merchantId)
    );
  }

  get currency() {
    if (
      this.componentWidget.getAttribute("currency") == "USD" ||
      this.componentWidget.getAttribute("currency") == "GBP" ||
      this.componentWidget.getAttribute("currency") == "CAD"
    ) {
      return this.componentWidget.getAttribute("currency");
    }
    return "USD";
  }

  get implementCloseModal() {
    return (
      this.componentWidget.getAttribute("implementCloseModal") == "true" ||
      this.componentWidget.getAttribute("implementclosemodal") == "true"
    );
  }

  get min() {
    if (!this.componentWidget.getAttribute("min")) return 35;
    else return Number(this.componentWidget.getAttribute("min"));
  }

  get minModal() {
    return (
      this.componentWidget.getAttribute("minModal") ||
      this.componentWidget.getAttribute("minmodal")
    );
  }

  get max() {
    if (!this.componentWidget.getAttribute("max")) return 1500;
    else return Number(this.componentWidget.getAttribute("max"));
  }

  get merchantId() {
    return (
      this.componentWidget.getAttribute("merchantId") ||
      this.componentWidget.getAttribute("merchantid")
    );
  }

  get amountSelector() {
    let selector =
      this.componentWidget.getAttribute("amountSelector") ||
      this.componentWidget.getAttribute("amountselector");
    return selector && document.querySelector(selector) ? selector : "";
  }

  get isServiceFeeMerchant() {
    return (
      this.componentWidget.getAttribute("isServiceFeeMerchant") === "true" ||
      this.componentWidget.getAttribute("isservicefeemerchant") === "true"
    );
  }

  get isMFPPMerchant() {
    return (
      this.componentWidget.getAttribute("isMFPPMerchant") === "true" ||
      this.componentWidget.getAttribute("ismfppmerchant") === "true"
    );
  }

  get installmentServiceFeeAmount() {
    return (
      this.componentWidget.getAttribute("installmentServiceFeeAmount") ||
      this.componentWidget.getAttribute("installmentservicefeeamount") ||
      1.0
    );
  }

  get alignment() {
    return this.componentWidget.getAttribute("alignment");
  }

  get singleLine() {
    return (
      this.componentWidget.getAttribute("single-line") === "true" ||
      this.componentWidget.getAttribute("singleLine") === "true"
    );
  }

  get isDarkMode() {
    return (
      this.componentWidget.getAttribute("darkMode") === "true" ||
      this.componentWidget.getAttribute("darkmode") === "true"
    );
  }

  get customMargins() {
    return (
      this.componentWidget.getAttribute("customMargins") ||
      this.componentWidget.getAttribute("custommargins")
    );
  }

  get size() {
    return this.componentWidget.getAttribute("size");
  }

  get priceColor() {
    return (
      this.componentWidget.getAttribute("priceColor") ||
      this.componentWidget.getAttribute("pricecolor")
    );
  }

  get breakpointAlignment() {
    return (
      this.componentWidget.getAttribute("breakpointAlignment") ||
      this.componentWidget.getAttribute("breakpointalignment")
    );
  }

  get disableAdaCompliance() {
    return (
      this.componentWidget.getAttribute("disableAdaCompliance") === "true" ||
      this.componentWidget.getAttribute("disableadacompliance") === "true"
    );
  }

  get isMobileSDK() {
    return (
      this.componentWidget.getAttribute("isMobileSDK") === "true" ||
      this.componentWidget.getAttribute("ismobilesdk") === "true"
    );
  }

  get learnMoreUrl() {
    return (
      this.componentWidget.getAttribute("learnMoreUrl") ||
      this.componentWidget.getAttribute("learnmoreurl")
    );
  }

  get subtextLayout() {
    // bandaid fix, since alignment and subtextLayout don't work together
    if (this.hasAlignment) {
      return false;
    }
    return (
      this.componentWidget.getAttribute("subtextLayout") ||
      this.componentWidget.getAttribute("subtextlayout")
    );
  }

  get is4Installments() {
    return this.widgetVerbiageAttribute === "4-installments";
  }

  get widgetVerbiageAttribute() {
    return (
      this.componentWidget.getAttribute("widgetVerbiage") ||
      this.componentWidget.getAttribute("widgetverbiage")
    );
  }

  get logoSize() {
    return (
      this.componentWidget.getAttribute("logoSize") ||
      this.componentWidget.getAttribute("logosize")
    );
  }

  get logoAlignment() {
    return (
      this.componentWidget.getAttribute("logoAlignment") ||
      this.componentWidget.getAttribute("logoalignment")
    );
  }

  get isLogoFirst() {
    return (
      ["logoFirst", "logofirst"].includes(this.displayMode) &&
      !this.subtextLayout
    );
  }

  get displayMode() {
    return (
      this.componentWidget.getAttribute("displayMode") ||
      this.componentWidget.getAttribute("displaymode")
    );
  }

  get logoColor() {
    const logoColor =
      this.componentWidget.getAttribute("logoColor") ||
      this.componentWidget.getAttribute("logocolor");
    const colorIsValidHexadecimal = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(
      logoColor
    );
    return colorIsValidHexadecimal ? logoColor : "";
  }

  get logoTextColor() {
    const logoTextColor =
      this.componentWidget.getAttribute("logoTextColor") ||
      this.componentWidget.getAttribute("logotextcolor");
    const colorIsValidHexadecimal = /^#[0-9a-f]{3}([0-9a-f]{3})?$/i.test(
      logoTextColor
    );
    return colorIsValidHexadecimal ? logoTextColor : "";
  }

  get logoOption() {
    return (
      this.componentWidget.getAttribute("logoOption") ||
      this.componentWidget.getAttribute("logooption")
    );
  }

  get hideLogo() {
    return (
      this.componentWidget.getAttribute("hideLogo") ||
      this.componentWidget.getAttribute("hidelogo")
    );
  }

  get hasAlignment() {
    return (
      ["left", "right", "center"].includes(this.alignment) && !this.singleLine
    );
  }

  get shouldShowWebbankDisclosure(): boolean {
    return this.isWebBankMerchant;
  }

  get shouldShowWebbankFeeDisclosure(): boolean {
    return this.isFeeMerchant && this.isWebBankMerchant;
  }

  // When there is change in price or subtotal, update installment price
  listenForAmountChange() {
    let selector = this.amountSelector;
    let separateMutationSelector = selector;

    // For the sites where the mutationObserver needs to be separate from the amount selector
    const amountSplit = selector.split(",");
    if (amountSplit.length >= 2) {
      selector = amountSplit[0];
      separateMutationSelector = amountSplit[1];
    }

    const observer = new MutationObserver((mutations) => {
      this.translate.translateComponent(this.currency, this.componentWidget);
      mutations.forEach(() => {
        const amountElement = document.querySelector(selector) as HTMLElement;
        const newAmountNum = parseFullAmount(amountElement.innerText);

        if (!isNaN(newAmountNum)) {
          this.handleAmountUpdate(newAmountNum);
        }
      });
    });

    const target = document.querySelector(separateMutationSelector);
    const options = {
      attributes: true,
      characterData: true,
      childList: true,
      subtree: true,
    };
    observer.observe(target, options);
  }

  getAmountSelectorValue() {
    const amountElement = document.querySelector(
      this.amountSelector
    ) as HTMLElement;
    const newAmountNum = parseFullAmount(amountElement.innerText);

    return newAmountNum;
  }

  // Should return fee per installment
  calculateCustomerFeeForThisOrder(amount) {
    let maxTier = 0;
    let maxFee = 0;
    this.merchantFeeTiers.forEach((tier) => {
      // checking all tiers for closest match in case tiers aren't ordered correctly
      const tierAmount = tier.feeStartsAt;
      if (tierAmount <= amount) {
        if (maxTier < tierAmount) {
          maxTier = tierAmount;
          maxFee = tier.totalFeePerOrder;
        }
      }
    });

    return maxFee / 4;
  }

  async handleAmountUpdate(newAmount) {
    this.initializeAmount(newAmount);
    await this.fetchPaymentSchedules();
    if (this.modalComponentObject) {
      this.modalComponentObject.totalAmount = this.totalAmount;
    }
  }

  async insertLogo() {
    try {
      // Add logo
      const logoContainer = this.$refs["qp-logo-container"] as Element;
      this.logoComponentObject = await _createLogo(
        logoContainer,
        Logo,
        this.widgetId
      );
      Object.keys(this.logoOptions).forEach((propName) => {
        this.logoComponentObject[propName] = this.logoOptions[propName];
      });
    } catch (error) {
      logger.error(error);
    }
  }

  async insertBaseModal() {
    try {
      this.modalComponentObject = await _createModal(
        "base",
        Modal,
        baseModalScss,
        this.widgetId
      );
      Object.keys(this.baseModalOptions).forEach((propName) => {
        this.modalComponentObject[propName] = this.baseModalOptions[propName];
      });
      this.modalType = Modal;
    } catch (error) {
      logger.error(error);
    }
  }

  async insertPayInNModal() {
    try {
      this.modalComponentObject = await _createModal(
        "payInN",
        PayInNModal,
        payInNModalScss,
        this.widgetId
      );
      this.modalComponentObject.widgetId = this.widgetId;
      this.modalType = PayInNModal;
    } catch (error) {
      logger.error(error);
    }
  }

  async insertInfoIcon() {
    try {
      const container = this.$refs["qp-info-icon-container"] as Element;
      this.infoIconComponentObject = await _createInfoIcon(container, InfoIcon);
      Object.keys(this.infoIconOptions).forEach((propName) => {
        this.infoIconComponentObject[propName] = this.infoIconOptions[propName];
      });
      this.infoIconComponentObject._wrapper.$on(
        "displayModal",
        this.displayModal
      );
    } catch (error) {
      logger.error(error);
    }
  }

  /**
   * Display modal and trigger verify widget if necessary
   */
  async displayModal() {
    await widgetApi.displayModal(this.widgetId);
    // Verify Widget if not already verified
    const hasCalledVerify = window.localStorage.getItem(
      this.configuration.verifyWidgetKey
    );

    if (!hasCalledVerify) {
      await this.verifyWidget();
    }

    if (this.hasModalOpened) {
      return;
    }

    const canPublishEvents =
      (this.configuration.isAnalyticsEnabled ||
        (this.fireEventsIfTriggerTermsExperiment &&
          this.configuration.experimentsForceEvents)) &&
      this.displayPromotions !== "display_promotions";
    if (canPublishEvents) {
      const amountToSend = this.totalAmount ? this.totalAmount : null;
      const domain = window.location.hostname;
      const path = window.location.pathname || "/";
      const widgetType = WidgetType.Standard;

      this.eventBus.publishViewedLearnMoreModalEvent(
        amountToSend,
        domain,
        path,
        widgetType,
        this.widgetOnlyVerbiage,
        this.widgetId
      );
    }

    if (this.displayPromotions === "display_promotions") {
      getWidgetData().viewedPromotionLearnMoreModal(
        this.widgetId,
        this.amount,
        this.url
      );
    }

    this.hasModalOpened = true;
  }

  hideModal() {
    widgetApi.hideModal(this.widgetId);
  }

  // Change alignment for media query
  handleMediaQuery() {
    const numbersonly = /[0-9]/g;
    const breakpoint = parseInt(
      this.breakpointAlignment.match(numbersonly).join(""),
      10
    );
    let breakpointAlign = this.breakpointAlignment.split(" ")[1];

    let alignPrefix = "flex-";
    let mediaQuerySpanStyling = "margin-left: 0;margin-right: 0.3em;";

    if (breakpointAlign === "center") {
      breakpointAlign = "center";
      alignPrefix = "";
    } else if (breakpointAlign === "left") {
      breakpointAlign = "start";
    } else if (breakpointAlign === "right") {
      breakpointAlign = "end";
      mediaQuerySpanStyling = "margin-left: 0.3em;margin-right: 0;";
    }

    const mediaQueryStyle = document.createElement("style");
    mediaQueryStyle.innerHTML = `
        @media screen and (max-width: ${breakpoint}px) {
          .qp-container {
            -webkit-box-pack: ${breakpointAlign};
            -ms-flex-pack: ${breakpointAlign};
            justify-content: ${alignPrefix}${breakpointAlign};
          }
          .qp--nowrap {
            ${mediaQuerySpanStyling}
          }
        }
      `;

    const siblingNode = this.componentWidget.shadowRoot.childNodes[0];
    this.componentWidget.shadowRoot.insertBefore(mediaQueryStyle, siblingNode);
  }

  /**
   * Calls the QP Gateway to verify widget integration
   * Only to be called from clicking the widget, for custom widget/modal combinations, manually verify widget
   */
  async verifyWidget() {
    const body = { verifyWidget: "true" };
    try {
      const url = `${this.configuration.gatewayHost}/mss/verify-widget`;
      await fetch(url, {
        method: "post",
        mode: "cors",
        cache: "no-cache",
        headers: {
          "Content-Type": "application/json",
        },
        referrerPolicy: "origin",
        body: JSON.stringify(body),
      });
      window.localStorage.setItem(this.configuration.verifyWidgetKey, "true");
    } catch (err) {
      // No useful logging, we still want to display modal
    }
  }

  private calculateWidgetVerbiage() {
    this.widgetVerbiageKey = widgetApi.getWidgetVerbiage(
      this.widgetVerbiageAttribute,
      this.isServiceFeeMerchant,
      this.isMFPPMerchant,
      this.isFeeMerchant
    );

    // This property says if there is a text variation from feature test
    this.isThereTextVariation =
      this.widgetVerbiageKey ===
        `widget.${ExperimentTextVariationKey.PayInInstallments}` ||
      this.widgetVerbiageKey ===
        `widget.${ExperimentTextVariationKey.BuyNowPayLater}`;

    // This property determines if we should show the installment amount on the widget
    this.shouldShowInstallmentAmount =
      this.widgetVerbiageKey !==
        `widget.${ExperimentTextVariationKey.PayInInstallments}` &&
      this.widgetVerbiageKey !==
        `widget.${ExperimentTextVariationKey.BuyNowPayLater}` &&
      this.widgetVerbiageKey !== "widget.4-installments";

    // For analytics events purposes we need to establish the variationText property based on the experiment
    if (!this.isThereTextVariation) {
      // if there is no text variation we must send this variation text for tracking purposes
      this.widgetOnlyVerbiage = "4-interest-free-payments";
    } else {
      // Extract the verbiage from the widgetVerbiageKey
      this.widgetOnlyVerbiage = this.widgetVerbiageKey.substring(7);
    }

    this.widgetVerbiageTranslated = this.translate.getSingleTranslation(
      this.widgetVerbiageKey
    );
  }

  private async setupWidgetData() {
    const widgetData = getWidgetData();

    if (this.shopifyWindowObject?.shop) {
      widgetData.setWebsiteDomain(this.shopifyWindowObject.shop);
    }

    if (isValidAmount(this.totalAmount)) {
      widgetData.setAmount(this.totalAmount.toString());
    }

    try {
      if (this.merchantId) {
        widgetData.setMerchantId(this.merchantId);

        if (this.displayPromotions == "display_promotions") {
          this.promotionObject = await promotions.fetchContentfulEntry(
            this.merchantId
          );
          this.promotionMinimum = !!this.promotionObject?.orderAmount;
          widgetData.viewedPromotionWidget(
            this.widgetId,
            this.totalAmount,
            this.url
          );
        } else if (this.displayPromotions == "hide_promotions") {
          widgetData.viewedStandardWidgetEvent(
            this.widgetId,
            this.totalAmount,
            this.url
          );
        }
      }

      await widgetData.load();

      this.paymentSchedules = widgetData.getPaymentSchedules();

      this.isPayIn8FeatureEnabled = widgetData.isFeatureEnabled(
        FeatureFlagKey.PayIn8Widget,
        false
      );

      this.isLearnMoreButtonFeatureEnabled = widgetData.isFeatureEnabled(
        FeatureFlagKey.WidgetLearnMoreButton,
        false
      );

      this.fireEventsIfTriggerTermsExperiment =
        widgetData.isFeatureEnabled(
          FeatureFlagKey.ForceBuyNowPayLaterTriggerTerm
        ) ||
        widgetData.isFeatureEnabled(FeatureFlagKey.ShowNewWidgetTriggerTerms);
    } catch (error) {
      logger.error(error.message);
    }
  }

  handleIntersection(
    entries: IntersectionObserverEntry[],
    observer: IntersectionObserver
  ) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const amountToSend = this.totalAmount ? this.totalAmount : null;
        const domain = window.location.hostname;
        const path = window.location.pathname || "/";
        const displayOptions = this.buildDisplayOptions();

        this.eventBus.publishViewedStandardWidgetEvent(
          amountToSend,
          domain,
          path,
          this.loadTimeMilliseconds,
          displayOptions,
          this.widgetOnlyVerbiage,
          this.widgetId
        );

        observer.disconnect();
      }
    });
  }

  private buildDisplayOptions() {
    const options: Options = {};

    const min = Number(this.componentWidget.getAttribute("min"));
    if (!isNaN(min) && min > 0) options.min = min;

    const max = Number(this.componentWidget.getAttribute("max"));
    if (!isNaN(max) && max > 0) options.max = max;

    const selector =
      this.componentWidget.getAttribute("languageSelector") ||
      this.componentWidget.getAttribute("languageselector");
    if (selector) options.languageSelector = selector;

    if (this.hasAlignment) options.alignment = this.alignment;
    if (this.breakpointAlignment)
      options.breakpointAlignment = this.breakpointAlignment;
    if (this.customMargins) options.customMargins = this.customMargins;
    if (this.disableAdaCompliance)
      options.disableAdaCompliance = this.disableAdaCompliance;
    if (this.displayMode) options.displayMode = this.displayMode;
    if (this.hideLogo === "true") options.hideLogo = true;
    if (this.isMFPPMerchant) options.isMFPPMerchant = this.isMFPPMerchant;
    if (this.learnMoreUrl) options.learnMoreUrl = this.learnMoreUrl;
    if (this.logoAlignment) options.logoAlignment = this.logoAlignment;
    if (this.logoOption) options.logoOption = this.logoOption;
    if (this.logoSize) options.logoSize = this.logoSize;
    if (this.minModal) options.minModal = this.minModal;
    if (this.priceColor) options.priceColor = this.priceColor;
    if (this.size) options.size = this.size;
    if (this.widgetVerbiageAttribute)
      options.widgetVerbiage = this.widgetVerbiageAttribute;

    return options;
  }
}
</script>
